import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import type { MenuItemVariantDto } from '../../../../../generated-api';
import { Dialog } from '@ordr-as/umami-react/dialog';
import { useIntl } from 'react-intl';
import GiftCardAmount from './GiftCardAmount';
import GiftCardPhoneNumber from './GiftCardPhoneNumber';

interface Props {
  item: MenuItemVariantDto;
  customPrice: number | undefined;
  setCustomPrice: Dispatch<SetStateAction<number | undefined>>;
  giftCardPhoneNumber: string | undefined;
  setGiftCardPhoneNumber: (phoneNumber?: string) => void;
  setOpenGiftCardDialog: Dispatch<SetStateAction<boolean>>;
  isOpen: boolean;
  addItemToCart: () => void;
}

const GiftCardDialog = (props: Props) => {
  const {
    item,
    customPrice,
    setCustomPrice,
    giftCardPhoneNumber,
    setGiftCardPhoneNumber,
    setOpenGiftCardDialog,
    isOpen,
    addItemToCart,
  } = props;
  const intl = useIntl();
  const [showPhoneNumberInput, setShowPhoneNumberInput] = useState(false);
  const [phoneNumberInput, setPhoneNumberInput] = useState<string | undefined>(undefined);

  if (!isOpen) {
    return null;
  }

  const handleOnClose = () => {
    setCustomPrice(undefined);
    setPhoneNumberInput(undefined);
    setOpenGiftCardDialog(false);
  };

  const handleSecondaryAction = () => {
    setPhoneNumberInput(undefined);
    setShowPhoneNumberInput(false);
  };

  const handlePrimaryAction = () => {
    if (giftCardPhoneNumber) {
      handleAddItemToCart();
      return;
    }

    if (!showPhoneNumberInput) {
      setShowPhoneNumberInput(true);
      return;
    }

    setGiftCardPhoneNumber(phoneNumberInput);
    handleAddItemToCart();
  };

  const handleAddItemToCart = () => {
    addItemToCart();
    handleOnClose();
  };

  const titleLabel = showPhoneNumberInput
    ? intl.formatMessage({ id: 'items.giftCard.dialog.phone.title' })
    : intl.formatMessage({ id: 'items.giftCard.dialog.amount.title' });

  const primaryActionLabel =
    showPhoneNumberInput || giftCardPhoneNumber
      ? intl.formatMessage({ id: 'generic.confirm' })
      : intl.formatMessage({ id: 'items.giftCard.dialog.amount.nextAction' });

  const isDisabledAction = showPhoneNumberInput ? !phoneNumberInput : !customPrice;

  const secondaryActionLabel = showPhoneNumberInput ? intl.formatMessage({ id: 'generic.back' }) : undefined;

  return (
    <Dialog
      aria-label="Gift card dialog"
      alignTop
      title={titleLabel}
      onClose={handleOnClose}
      actionLabel={primaryActionLabel}
      onAction={handlePrimaryAction}
      isDisabledAction={isDisabledAction}
      secondaryActionLabel={secondaryActionLabel}
      onSecondaryAction={handleSecondaryAction}
    >
      {showPhoneNumberInput && customPrice ? (
        <GiftCardPhoneNumber setPhoneNumber={setPhoneNumberInput} customPrice={customPrice} error={false} />
      ) : (
        <GiftCardAmount item={item} customPrice={customPrice} setCustomPrice={setCustomPrice} />
      )}
    </Dialog>
  );
};

export default GiftCardDialog;
